/**
 * Rule: inventory follows the current selection; no selection clears and hides the notice,
 * and recognised variants restore visibility according to the stock-notice settings.
 * Blocks without inventory data or a notice stay as rendered.
 */
if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      // Liquid uses visibility-hidden until this block initialises.
      if (this.classList.contains('visibility-hidden')) {
        this.initLazySection();
      } else {
        window.initLazyScript(this, this.initLazySection.bind(this));
      }
    }

    initLazySection() {
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');
      this.variantInventory = this.getVariantInventory();
      if (!this.variantInventory.length || !this.inventoryNotice) return;
      // Use hidden for all block visibility after init; do not retain two CSS hide switches.
      if (this.classList.contains('visibility-hidden')) {
        this.hidden = true;
        this.classList.remove('visibility-hidden');
      }

      // Bind to on:variant:change event for this product. The standard picker dispatches it on
      // itself; the modular pickers (variant-picker-single/-multi) dispatch it on the product
      // section, so listen there when the page has no standard picker. One source only, so a
      // bubbling standard-picker event is never handled twice.
      const variantPicker = this.closest('.product-info')?.querySelector('variant-picker');
      const eventSource = variantPicker || this.closest('.js-product');
      if (eventSource) {
        eventSource.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      // Events can fire before lazy init. The standard picker can retain a stale form ID
      // for an impossible combination, so consult its actual selection, including no match.
      const selectedId = this.getSelectedVariantId(variantPicker);
      if (selectedId === null) {
        this.handleVariantChange({ detail: { variant: null } });
        return;
      }

      // Progressive enhancement: Check if content is already server-side rendered
      // If status text exists, skip initial render to prevent flash
      const existingContent = this.inventoryNotice?.textContent?.trim();

      if (!existingContent) {
        // No server-side content - render via JS (fallback for variant changes)
        this.updateInventory(
          parseInt(this.dataset.inventoryQuantity, 10),
          this.dataset.variantAvailable === 'true',
          this.dataset.inventoryPolicy
        );
      }

      // This block initialises only when scrolled near, so a shopper may already have picked
      // another variant and its event fired before the listener above existed.
      const selected = this.variantInventory.find((variant) => variant.id === selectedId);
      if (selected) {
        this.currentVariantId = selectedId;
        const rendered =
          Math.max(selected.inventory_quantity, 0) === parseInt(this.dataset.inventoryQuantity, 10) &&
          selected.available === (this.dataset.variantAvailable === 'true') &&
          selected.inventory_policy === this.dataset.inventoryPolicy;
        if (!rendered) {
          this.updateInventory(
            selected.inventory_quantity,
            selected.available,
            selected.inventory_policy
          );
        }
      }
    }

    /**
     * The standard picker's actual selection, or the modular picker's main-form control.
     * Empty picker controls mean no selection even when another input retains an old ID.
     * @param {HTMLElement|null} variantPicker - The standard picker, if present.
     * @returns {?number}
     */
    getSelectedVariantId(variantPicker) {
      if (typeof variantPicker?.getSelectedVariant === 'function') {
        const id = Number(variantPicker.getSelectedVariant()?.id);
        return id > 0 ? id : null;
      }
      const section = this.closest('.js-product');
      if (!section) return null;
      const fields = Array.from(
        section.querySelectorAll('input[name="id"], select[name="id"]')
      ).filter(
        (field) =>
          !field.disabled && field.form?.classList.contains('js-product-form-main')
      );
      // Dropdown deselection does not clear the hidden ID; reset does. Keep both empty states.
      const field = fields.find((el) => el.hasAttribute('data-variant-dropdown')) ||
        fields.find((el) => el.hasAttribute('data-variant-id-input')) || fields[0];
      const id = Number(field?.value);
      return id > 0 ? id : null;
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      return this.variantInventory || JSON.parse(dataEl?.textContent ?? '[]');
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail?.variant) {
        const inventory = this.variantInventory.filter(
          (variant) => variant.id === evt.detail.variant.id
        )[0];
        // Not one of this product's variants: leave the notice as it is.
        if (!inventory) return;
        // The multi-option picker can announce the same variant twice for one click
        // (its own match, then the auto-selected option's response): show it once.
        if (evt.detail.variant.id === this.currentVariantId) return;
        this.currentVariantId = evt.detail.variant.id;
        this.updateInventory(
          inventory.inventory_quantity,
          inventory.available,
          inventory.inventory_policy
        );
      } else {
        this.currentVariantId = null;
        if (this.inventoryNotice?.textContent !== undefined) this.inventoryNotice.innerText = '';
        if (this.urgencyMessage) this.urgencyMessage.innerHTML = '';
        if (this.indicatorBar) this.indicatorBar.hidden = true;
        this.removeAttribute('data-inventory-level');
        this.hidden = true;
      }
    }

    /**
     *
     * Updates the inventory notice
     * @param {number} count - the inventory quantity available
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     */
    updateInventory(count, available, inventoryPolicy) {
      let inventoryLevel;

      if (count <= 0) {
        if (inventoryPolicy === 'continue') {
          inventoryLevel = 'backordered';
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else if (count <= parseInt(this.dataset.thresholdVeryLow, 10)) {
        inventoryLevel = 'very_low';
      } else if (count <= parseInt(this.dataset.thresholdLow, 10)) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      if (((this.dataset.showNotice === 'always'
        || (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')))
        && !(inventoryLevel === 'backordered' && this.dataset.showNoStockBackordered === 'false'))) {
        this.hidden = false;

        // Set the inventory level data attribute
        this.setAttribute('data-inventory-level', inventoryLevel);

        // Determine whether to show the count or not
        if (inventoryLevel === 'backordered') {
          this.inventoryNotice.innerText = '';
        } else if (count === 1) {
          this.inventoryNotice.innerText = 'Only 1 left!';
        } else if (inventoryLevel !== 'in_stock' && (this.dataset.showCount === 'always' || (this.dataset.showCount === 'low' && inventoryLevel.includes('low')))) {
          this.inventoryNotice.innerText = theme.strings.onlyXLeft.replace('[quantity]', count);
        } else if (inventoryLevel === 'very_low') {
          this.inventoryNotice.innerText = theme.strings.veryLowStock;
        } else if (inventoryLevel === 'low') {
          this.inventoryNotice.innerText = theme.strings.lowStock;
        } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
          this.inventoryNotice.innerText = theme.strings.inStock;
        } else if (inventoryLevel === 'none') {
          this.inventoryNotice.innerText = theme.strings.noStock;
        }

        // Update urgency message if needed
        if (this.urgencyMessage) {
          if (inventoryLevel === 'very_low') {
            this.urgencyMessage.innerHTML = this.dataset.textVeryLow;
          } else if (inventoryLevel === 'low') {
            this.urgencyMessage.innerHTML = this.dataset.textLow;
          } else if (inventoryLevel === 'backordered') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered;
          } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
            this.urgencyMessage.innerHTML = this.dataset.textNormal;
          } else if (inventoryLevel === 'none') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          }
        }

        // Update the bar indicator
        if (this.indicatorBar) {
          this.indicatorBar.hidden = false;

          let newWidth;
          if ((count >= this.dataset.scale || inventoryLevel === 'in_stock') && inventoryLevel !== 'backordered') {
            newWidth = 100;
          } else {
            newWidth = ((100 / parseInt(this.dataset.scale, 10)) * count).toFixed(1);
          }
          this.indicatorBar.querySelector('span').style.width = `${newWidth}%`;
        }
      } else {
        this.hidden = true;
      }
    }
  }

  customElements.define('product-inventory', ProductInventory);
}
